import { useState, useEffect } from 'react';
import { Clock, Calendar, Repeat, FileText } from 'lucide-react';
import Modal from './Modal';
import { TIME_BLOCK_NAMES } from '../hooks/useGoogleCalendar';

// Color map for time block types - Rich colors for light mode
const BLOCK_COLORS = {
  'Office Time': '#C45D3A',
  'Plan & Organize': '#7C5CBF',
  'Projects': '#3B82C4',
  'Delegate and Inspect': '#2D8A5F',
  'Morning Start': '#C44D7A',
  'EOD wrap-up': '#5B6BD4'
};

// Recurrence options
const RECURRENCE_OPTIONS = [
  { value: '', label: 'Does not repeat' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'WEEKDAYS', label: 'Every weekday (Mon-Fri)' },
  { value: 'BIWEEKLY', label: 'Every 2 weeks' },
  { value: 'MONTHLY', label: 'Monthly' }
];

export default function TimeBlockModal({ isOpen, onClose, onSave, selectedDate, prefillTime }) {
  const [formData, setFormData] = useState({
    name: TIME_BLOCK_NAMES[0],
    startTime: '09:00',
    endTime: '10:00',
    description: '',
    recurrence: ''
  });

  useEffect(() => {
    if (isOpen) {
      // Reset to defaults when modal opens, but use prefillTime if provided
      setFormData({
        name: TIME_BLOCK_NAMES[0],
        startTime: prefillTime?.startTime || '09:00',
        endTime: prefillTime?.endTime || '10:00',
        description: '',
        recurrence: ''
      });
    }
  }, [isOpen, prefillTime]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const formatDate = (date) => {
    if (!date) return 'Today';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Time Block"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Selected Date Display */}
        <div className="flex items-center gap-2 p-3 bg-surface-hover rounded-lg">
          <Calendar size={16} className="text-accent" />
          <span className="text-sm text-text-primary">{formatDate(selectedDate)}</span>
        </div>

        {/* Block Type Selection */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Time Block Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {TIME_BLOCK_NAMES.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setFormData({ ...formData, name })}
                className={`p-3 rounded-lg border text-left transition-all flex items-center gap-2 ${
                  formData.name === name
                    ? 'border-accent bg-accent-muted'
                    : 'border-border bg-surface-elevated hover:bg-surface-hover'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: BLOCK_COLORS[name] || '#6B7280' }}
                />
                <span className="text-sm text-text-primary">{name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Time Range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              <Clock size={14} className="inline mr-1.5" />
              Start Time
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              required
              className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              End Time
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              required
              className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            <FileText size={14} className="inline mr-1.5" />
            Description (optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Add notes, agenda, or details..."
            rows={3}
            className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all resize-none"
          />
        </div>

        {/* Recurrence */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            <Repeat size={14} className="inline mr-1.5" />
            Repeat
          </label>
          <select
            value={formData.recurrence}
            onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
            className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all cursor-pointer"
          >
            {RECURRENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-5 bg-surface-hover text-text-secondary border border-border rounded-xl text-sm font-medium cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-5 bg-accent hover:bg-orange-hover text-white rounded-xl text-sm font-semibold cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            Add to Calendar
          </button>
        </div>
      </form>
    </Modal>
  );
}
